/**
 * Sport grouping. intervals.icu reports ~60 activity types; the dashboard reasons about
 * six groups. Ironman training is swim/bike/run plus strength; walks and hikes are their own
 * group so they never count as running; everything else is "other".
 */
export const SPORT_GROUPS = ['swim', 'bike', 'run', 'walk', 'strength', 'other'] as const
export type SportGroup = (typeof SPORT_GROUPS)[number]

const SWIM = new Set(['Swim', 'OpenWaterSwim'])
const BIKE = new Set([
  'Ride',
  'VirtualRide',
  'GravelRide',
  'MountainBikeRide',
  'EBikeRide',
  'EMountainBikeRide',
  'TrackRide',
  'Handcycle',
  'Velomobile',
])
const RUN = new Set(['Run', 'VirtualRun', 'TrailRun'])
const WALK = new Set(['Walk', 'Hike'])
const STRENGTH = new Set(['WeightTraining', 'Crossfit', 'HighIntensityIntervalTraining', 'Workout'])

export function sportGroupFor(type: string | null | undefined): SportGroup {
  if (!type) return 'other'
  if (SWIM.has(type)) return 'swim'
  if (BIKE.has(type)) return 'bike'
  if (RUN.has(type)) return 'run'
  if (WALK.has(type)) return 'walk'
  if (STRENGTH.has(type)) return 'strength'
  return 'other'
}

/**
 * True for sessions measured in pace on foot: runs, walks and hikes. Use this for display
 * (pace, steps per minute), never for volume or records, which use the sport group.
 */
export function isOnFoot(a: { sportGroup: SportGroup }): boolean {
  return a.sportGroup === 'run' || a.sportGroup === 'walk'
}

export const SPORT_GROUP_LABEL: Record<SportGroup, string> = {
  swim: 'Swim',
  bike: 'Bike',
  run: 'Run',
  walk: 'Walk',
  strength: 'Strength',
  other: 'Other',
}

/** Groups that count toward triathlon volume. */
export const TRI_GROUPS: readonly SportGroup[] = ['swim', 'bike', 'run']

// ───────────────────────────── Indoor, virtual and assisted sessions ─────────────────────────────

/** Ride types whose speed and distance are not the athlete's own on the road. */
const NON_OUTDOOR_RIDE_TYPES = new Set(['VirtualRide', 'EBikeRide', 'EMountainBikeRide'])

type SessionFlags = { type: string; sportGroup: SportGroup; trainer: boolean; indoor: boolean }

/**
 * A ride whose speed and distance mean something: not virtual, not an e-bike, not on a trainer
 * or flagged indoor. Speed and distance records, the 30 minute ride marker and race-leg
 * estimates use only these.
 */
export function isOutdoorRide(a: SessionFlags): boolean {
  return a.sportGroup === 'bike' && !NON_OUTDOOR_RIDE_TYPES.has(a.type) && !a.trainer && !a.indoor
}

/** A run outdoors: not virtual, not on a treadmill or flagged indoor. */
export function isOutdoorRun(a: SessionFlags): boolean {
  return a.sportGroup === 'run' && a.type !== 'VirtualRun' && !a.trainer && !a.indoor
}

/** A pool swim (intervals.icu type "Swim"); open-water swims are "OpenWaterSwim" and GPS-measured. */
export function isPoolSwim(a: { type: string }): boolean {
  return a.type === 'Swim'
}

/**
 * A recording the athlete never trained in: under a minute moving with no distance (a watch
 * started and stopped by accident, like a 7 s, 0 m pool swim). Left out of the sessions that
 * analysis reads (counts, streaks, load, compliance); still listed with the activities.
 */
export function isStrayRecording(a: {
  movingTime: number | null
  distance: number | null
}): boolean {
  return (a.movingTime ?? 0) < 60 && !((a.distance ?? 0) > 0)
}

// ───────────────────────────── Plausible efforts ─────────────────────────────

/**
 * Fastest believable average speed, m/s, for an effort covering `metres`: past this it is a GPS
 * glitch, not a performance. Run: 7 m/s (a 57 s 400 m) up to 1 km, 6.5 m/s (2:34 per km) from
 * 1 km on. Swim: 2.2 m/s (0:45 per 100 m). Bike: 25 m/s (90 km/h). Generous for any amateur,
 * well under what a GPS jump produces.
 */
export function effortSpeedCeiling(sportGroup: SportGroup, metres: number): number {
  switch (sportGroup) {
    case 'run':
    case 'walk':
      return metres >= 1000 ? 6.5 : 7
    case 'swim':
      return 2.2
    default:
      return 25
  }
}

/**
 * Fastest believable speed between two consecutive stream samples, m/s. Looser than the effort
 * ceiling (a sprint finish, a descent), it only catches teleporting GPS points.
 */
export function sampleSpeedCeiling(sportGroup: SportGroup): number {
  switch (sportGroup) {
    case 'run':
    case 'walk':
      return 10
    case 'swim':
      return 3
    default:
      return 30
  }
}

/** Plausible average heart rate bounds, bpm. */
const EFFORT_HR_BOUNDS: [number, number] = [30, 230]

/**
 * False for a stored best effort no human produced: a time over a distance or an average speed
 * above `effortSpeedCeiling`, a non-positive value, or a heart rate outside 30..230 bpm. Use it
 * wherever stored efforts are ranked (records, the PR feed, marker goals, race-leg estimates);
 * new efforts are already filtered when computed.
 */
export function isPlausibleEffort(e: {
  sportGroup: SportGroup
  kind: 'distance' | 'duration'
  target: number
  metric: 'time' | 'heartrate' | 'watts' | 'speed'
  value: number
}): boolean {
  if (!Number.isFinite(e.value) || e.value <= 0 || !(e.target > 0)) return false
  if (e.kind === 'distance' && e.metric === 'time') {
    return e.target / e.value <= effortSpeedCeiling(e.sportGroup, e.target)
  }
  if (e.metric === 'speed') {
    return e.value <= effortSpeedCeiling(e.sportGroup, e.value * e.target)
  }
  if (e.metric === 'heartrate')
    return e.value >= EFFORT_HR_BOUNDS[0] && e.value <= EFFORT_HR_BOUNDS[1]
  return true
}
